import type { OnChangeFn, SortingState } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import type { ColumnVisibilityState } from '../../../lib/hubspot-management/columnVisibility';
import type { HubspotTicketDetail } from '../../../stores/hubspotManagementStore';
import { DataTable } from '../../common/DataTable';
import {
  HubspotTicketPager,
  type HubspotTicketPagination,
  HubspotTicketsEmptyState,
  HubspotTicketsListHeader,
} from '../list/HubspotTicketList';
import {
  ChecksChip,
  HubspotAddressesCell,
  HubspotAssigneeCell,
  HubspotDateCell,
  HubspotTicketIdCell,
  HubspotVersionAndSizeCell,
  SourceChip,
  StatusChip,
  SubmissionIdentity,
  SubmissionKindChip,
  TicketAge,
} from './HubspotTicketCells';

interface HubspotTicketsTableProps {
  tickets: HubspotTicketDetail[];
  sorting: SortingState;
  pagination: HubspotTicketPagination;
  isLoading: boolean;
  selectedTicketId: string | null;
  columnVisibility: ColumnVisibilityState;
  hasActiveFilters: boolean;
  onReload: () => void;
  onSelectTicket: (ticketId: string) => void;
  onSortingChange: OnChangeFn<SortingState>;
  onColumnVisibilityChange: OnChangeFn<ColumnVisibilityState>;
  onPageChange: (page: number) => void;
  onClearFilters: () => void;
}

const headerClassName =
  'text-[11px] font-semibold uppercase tracking-wide text-neutral-400';

const column = (
  key: string,
  header: string,
  size: number,
  options: { sortable?: boolean; hideable?: boolean; descFirst?: boolean },
  accessor: (ticket: HubspotTicketDetail) => ReactNode,
) => ({
  key,
  header,
  headerClassName,
  className: '!px-4',
  size,
  enableSorting: options.sortable ?? false,
  enableHiding: options.hideable ?? true,
  sortDescFirst: options.descFirst,
  accessor,
});

/** Wide, sortable table used on desktop while no ticket is open. */
export function HubspotTicketsTable({
  tickets,
  sorting,
  pagination,
  isLoading,
  selectedTicketId,
  columnVisibility,
  hasActiveFilters,
  onReload,
  onSelectTicket,
  onSortingChange,
  onColumnVisibilityChange,
  onPageChange,
  onClearFilters,
}: HubspotTicketsTableProps) {
  const columns = [
    column(
      'dapp',
      'Submission',
      340,
      { sortable: true, hideable: false },
      (ticket) => (
        <div className="flex items-center gap-3">
          <SubmissionIdentity ticket={ticket} />
          <div className="ml-auto hidden shrink-0 items-center gap-1 2xl:flex">
            <SubmissionKindChip ticket={ticket} />
            <SourceChip source={ticket.submissionSource} />
          </div>
        </div>
      ),
    ),
    column('status', 'Status', 150, { sortable: true }, (ticket) =>
      ticket.mappedSubmissionStatus ? (
        <StatusChip ticket={ticket} />
      ) : (
        <span className="text-xs text-neutral-500">—</span>
      ),
    ),
    column('checks', 'Checks', 150, {}, (ticket) => (
      <ChecksChip summary={ticket.automatedReviewSummary} />
    )),
    column('assignee', 'Assignee', 170, {}, (ticket) => (
      <HubspotAssigneeCell ticket={ticket} />
    )),
    column(
      'createdAt',
      'Created',
      110,
      { sortable: true, descFirst: true },
      (ticket) => (
        <TicketAge
          date={ticket.createdAt}
          className="text-xs text-neutral-300"
        />
      ),
    ),
    column('ticket', 'Ticket ID', 130, { sortable: true }, (ticket) => (
      <HubspotTicketIdCell ticket={ticket} />
    )),
    column('version', 'Version / Size', 140, { sortable: true }, (ticket) => (
      <HubspotVersionAndSizeCell ticket={ticket} />
    )),
    column('addresses', 'NFT Addresses', 200, { sortable: true }, (ticket) => (
      <HubspotAddressesCell ticket={ticket} />
    )),
    column(
      'closedDate',
      'Closed',
      170,
      { sortable: true, descFirst: true },
      (ticket) => (
        <HubspotDateCell date={ticket.closedDate} className="text-green-400" />
      ),
    ),
    column(
      'lastModifiedAt',
      'Last Modified',
      170,
      { sortable: true, descFirst: true },
      (ticket) => <HubspotDateCell date={ticket.lastModifiedAt} />,
    ),
  ];

  const isEmpty = !isLoading && tickets.length === 0;

  return (
    <div className="overflow-hidden rounded-xl border border-neutral-800 bg-neutral-900/60">
      <HubspotTicketsListHeader
        total={pagination.total}
        isLoading={isLoading}
        onReload={onReload}
      />
      <div className="border-t border-neutral-800">
        {isEmpty ? (
          <HubspotTicketsEmptyState
            hasActiveFilters={hasActiveFilters}
            onClearFilters={onClearFilters}
          />
        ) : (
          <DataTable
            data={tickets}
            columns={columns}
            isLoading={isLoading}
            onRowClick={(ticket) => onSelectTicket(ticket.id)}
            selectedRowId={selectedTicketId ?? undefined}
            getRowId={(ticket) => ticket.id}
            enableSorting={true}
            sorting={sorting}
            onSortingChange={onSortingChange}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={onColumnVisibilityChange}
            headerRowClassName="bg-neutral-900/80"
          />
        )}
      </div>
      <HubspotTicketPager pagination={pagination} onPageChange={onPageChange} />
    </div>
  );
}
